import { EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Flex, DatePicker, Table } from 'antd'
import { ColumnType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useTreatmentServiceDeleteConsultationEndpoint,
  useTreatmentServiceGetConsultationListEndpoint,
  useTreatmentServiceGetConsultationListEndpointKey,
  useTreatmentServiceGetEpidemiologyCaseFormDraftEndpoint,
  useTreatmentServiceGetEpidemiologyCaseFormDraftEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  ConsultationSortBy,
  ConsultationStatus,
  GetConsultationListResponseItem
} from 'src/api/requests'
import { Refresh2Icon } from 'src/assets'
import {
  DeleteConfirm,
  PageBreadcrumbs,
  Select,
  TableActionButtons,
  message
} from 'src/components'
import {
  ScheduleConsultationModal,
  useConfig
} from 'src/features/persons/components'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import {
  formErrorHandler,
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

import { useColumns } from '../hooks'

export const InstitutionConsultationsPage = () => {
  const { t } = useLocales()
  const { userData } = useUser()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const pagination = useTablePagination()
  const { options } = useConfig()
  const { columns } = useColumns()

  const [date, setDate] = useState('')
  const [ordering, setOrdering] = useState('')

  const [isOpen, setIsOpen] = useState(false)
  const [consultationId, setConsultationId] = useState<string>()
  const [personId, setPersonId] = useState<string>()
  const [isEpidemiologyClicked, setIsEpidemiologyClicked] = useState(false)

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(!!ordering && {
      sortBy: normalizeOrdering(ordering) as ConsultationSortBy,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(!!date && { consultationDate: date }),
    legalEntityId: userData?.currentLegalEntityId
  }

  const { data, isLoading } =
    useTreatmentServiceGetConsultationListEndpoint(queryParams)

  const { data: epidemiologyCase, error: epidemiologyError } =
    useTreatmentServiceGetEpidemiologyCaseFormDraftEndpoint(
      { consultationId: String(consultationId) },
      [consultationId],
      { enabled: !!consultationId && isEpidemiologyClicked }
    )

  const { mutate: deleteMutation } =
    useTreatmentServiceDeleteConsultationEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        message.success(t('consultations.success_delete_consultation'))
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetConsultationListEndpointKey]
        })
      }
    })

  const onSelect = (val: string) => {
    const isConsultationType = val.includes('consultationType')
    const isDesc = val.includes('-')

    if (isConsultationType) {
      setOrdering(
        isDesc
          ? `-${ConsultationSortBy.CONSULTATION_TYPE}`
          : ConsultationSortBy.CONSULTATION_TYPE
      )
    } else setOrdering(val)

    resetPagination(pagination)
  }

  const getActionItems = useMemo(
    () =>
      (
        id: string,
        status: ConsultationStatus,
        canAddEpidemiologyCase: boolean,
        personId?: string,
        canEdit?: boolean
      ) => {
        const isPlanned = status === ConsultationStatus.SCHEDULED
        const isFinalized = status === ConsultationStatus.PRESENT

        const onEdit = () => {
          if (personId) navigate(paths.persons.consultations.edit(personId, id))
        }

        return [
          ...(isPlanned
            ? [
                {
                  key: 'reschedule',
                  label: t('consultations.reschedule_consultation'),
                  icon: <Refresh2Icon />,
                  onClick: () => {
                    setConsultationId(id)
                    setIsOpen(true)
                  }
                },
                {
                  key: 'edit',
                  label: t('consultations.manage_consultation'),
                  onClick: onEdit,
                  icon: <EditOutlined />
                }
              ]
            : []),
          // A performed consultation: only its author, on the day it was performed.
          ...(isFinalized && canEdit
            ? [
                {
                  key: 'edit-performed',
                  label: t('common.edit'),
                  onClick: onEdit,
                  icon: <EditOutlined />
                }
              ]
            : []),
          ...(isFinalized && canAddEpidemiologyCase
            ? [
                {
                  key: 'epidemiology',
                  label: t('epidemiology.add_epidemiology_case'),
                  icon: <PlusOutlined />,
                  onClick: () => {
                    setIsEpidemiologyClicked(true)
                    setConsultationId(id)
                    setPersonId(personId)
                  }
                }
              ]
            : []),
          {
            key: 'details',
            label: t('common.details'),
            icon: <EyeOutlined />,
            onClick: () => {
              if (personId)
                navigate(paths.persons.consultations.details(personId, id))
            }
          },
          ...(isPlanned
            ? [
                {
                  key: 'delete',
                  label: (
                    <DeleteConfirm
                      title={t('consultations.delete_consultation')}
                      onConfirm={() => deleteMutation({ id })}
                    />
                  ),
                  danger: true
                }
              ]
            : [])
        ]
      },
    [t]
  )

  const tabelColumns: ColumnType<GetConsultationListResponseItem>[] = [
    ...columns,
    {
      key: 'actions',
      fixed: 'right',
      render: ({ id, status, canAddEpidemiologyCase, person, canEdit }) => (
        <TableActionButtons
          justify="center"
          items={getActionItems(
            id,
            status,
            canAddEpidemiologyCase,
            person?.id,
            canEdit
          )}
        />
      )
    }
  ]

  useEffect(() => {
    if (epidemiologyError && epidemiologyError instanceof ApiError) {
      formErrorHandler(epidemiologyError.body.errors)
      setIsEpidemiologyClicked(false)
      setPersonId(undefined)
      queryClient.removeQueries({
        queryKey: [useTreatmentServiceGetEpidemiologyCaseFormDraftEndpointKey]
      })
    }
  }, [epidemiologyError, epidemiologyCase])

  useEffect(() => {
    if (epidemiologyCase) {
      navigate(
        paths.patients.epidemiologyCases.add(
          String(personId),
          String(consultationId)
        )
      )
    }
  }, [epidemiologyCase])

  return (
    <PageBreadcrumbs
      title={t('persons.person_consultations')}
      items={[{ title: t('persons.person_consultations') }]}
    >
      <Flex vertical gap={16}>
        <Flex justify="space-between">
          <Flex gap={8}>
            <DatePicker
              format={dayMonthYearFormatWithDot}
              onChange={(val) =>
                setDate(val ? dayjs(val).format(dayMonthYearFormatWithDot) : '')
              }
            />

            <Select onSelect={onSelect} options={options} />
          </Flex>
        </Flex>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={tabelColumns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />

        {!!consultationId && isOpen && (
          <ScheduleConsultationModal
            onCancel={() => {
              setConsultationId(undefined)
              setIsOpen(false)
            }}
            consultationId={consultationId}
          />
        )}
      </Flex>
    </PageBreadcrumbs>
  )
}
